import React, { useEffect, useRef, useState, useCallback } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { useLocation } from "react-router-dom";

const QRDownload = () => {
  const location = useLocation();
  const canvasRef = useRef(null);
  const qrCanvasRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const [isGenerating, setIsGenerating] = useState(true);
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [qrReady, setQrReady] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [previewGenerated, setPreviewGenerated] = useState(false);

  const generateFramedQR = useCallback((targetCanvas) => {
    const canvas = targetCanvas || canvasRef.current;
    const qrCanvas = qrCanvasRef.current?.querySelector("canvas");
    
    if (!canvas || !qrCanvas) {
      console.log("⏳ Aguardando renderização do QR Code...");
      return false;
    }

    try {
      // CONFIGURAÇÕES PARA IMPRESSÃO PROFISSIONAL
      // Resolução: 300 DPI (padrão profissional)
      // Tamanho: 15cm x 21cm (aproximadamente A5)
      const DPI = 300;
      const cmToPixels = (cm) => Math.round((cm / 2.54) * DPI);
      
      const config = {
        // Dimensões em pixels para 300 DPI
        width: cmToPixels(15),      // ~1772 pixels
        height: cmToPixels(21),     // ~2480 pixels
        bleed: cmToPixels(0.3),     // Sangria de 3mm
        
        // QR Code grande para fácil escaneamento
        qrSize: cmToPixels(8),      // 8cm de QR (bem legível)
        
        // Cores otimizadas para impressão (próximas ao CMYK)
        colors: {
          background: "#FFFFFF",
          primary: "#1A1A1A",       // Preto rico
          accent: "#C9A961",        // Dourado mais suave para impressão
          accentDark: "#9D7E3A",    // Dourado escuro
          text: "#2C2C2C"           // Cinza muito escuro
        }
      };

      // Posição centralizada do QR Code
      config.qrPosition = {
        x: (config.width - config.qrSize) / 2,
        y: cmToPixels(6)  // 6cm do topo
      };

      // Configurar dimensões do canvas (com sangria)
      const totalWidth = config.width + (config.bleed * 2);
      const totalHeight = config.height + (config.bleed * 2);
      
      canvas.width = totalWidth;
      canvas.height = totalHeight;
      const ctx = canvas.getContext("2d");

      // Offset para sangria
      const offset = config.bleed;

      // 1. FUNDO BRANCO COM SANGRIA
      ctx.fillStyle = config.colors.background;
      ctx.fillRect(0, 0, totalWidth, totalHeight);

      // 2. BORDAS DECORATIVAS TRIPLAS (mais elegante)
      const borderMargin = cmToPixels(0.8);
      
      // Borda externa dourada grossa
      ctx.strokeStyle = config.colors.accent;
      ctx.lineWidth = cmToPixels(0.15);
      ctx.strokeRect(
        offset + borderMargin,
        offset + borderMargin,
        config.width - (borderMargin * 2),
        config.height - (borderMargin * 2)
      );
      
      // Borda interna dourada fina
      const innerBorder = borderMargin + cmToPixels(0.4);
      ctx.lineWidth = cmToPixels(0.06);
      ctx.strokeRect(
        offset + innerBorder,
        offset + innerBorder,
        config.width - (innerBorder * 2),
        config.height - (innerBorder * 2)
      );

      // Borda de detalhe dourado escuro
      const detailBorder = borderMargin + cmToPixels(0.2);
      ctx.strokeStyle = config.colors.accentDark;
      ctx.lineWidth = cmToPixels(0.03);
      ctx.strokeRect(
        offset + detailBorder,
        offset + detailBorder,
        config.width - (detailBorder * 2),
        config.height - (detailBorder * 2)
      );

      // Centro horizontal para alinhamento de texto
      const centerX = offset + (config.width / 2);

      // 3. CABEÇALHO "SOCIALITE"
      ctx.fillStyle = config.colors.primary;
      ctx.font = `bold ${cmToPixels(1.5)}px Georgia, serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("SOCIALITE", centerX, offset + cmToPixels(3));

      // Linha decorativa sob o logo
      const lineY = offset + cmToPixels(3.8);
      ctx.beginPath();
      ctx.moveTo(centerX - cmToPixels(3), lineY);
      ctx.lineTo(centerX + cmToPixels(3), lineY);
      ctx.strokeStyle = config.colors.accent;
      ctx.lineWidth = cmToPixels(0.04);
      ctx.stroke();

      // Subtítulo decorativo
      ctx.font = `italic ${cmToPixels(0.6)}px Georgia, serif`;
      ctx.fillStyle = config.colors.accent;
      ctx.fillText("✦ Menu Digital ✦", centerX, offset + cmToPixels(4.7));

      // 4. MOLDURA BRANCA PARA QR CODE (fundo limpo)
      const qrPadding = cmToPixels(0.3);
      ctx.fillStyle = "#FFFFFF";
      ctx.shadowColor = "rgba(0, 0, 0, 0.15)";
      ctx.shadowBlur = cmToPixels(0.2);
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = cmToPixels(0.1);
      
      ctx.fillRect(
        offset + config.qrPosition.x - qrPadding,
        offset + config.qrPosition.y - qrPadding,
        config.qrSize + (qrPadding * 2),
        config.qrSize + (qrPadding * 2)
      );
      
      // Resetar sombra
      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;

      // Borda fina dourada ao redor do QR
      ctx.strokeStyle = config.colors.accent;
      ctx.lineWidth = cmToPixels(0.04);
      ctx.strokeRect(
        offset + config.qrPosition.x - qrPadding,
        offset + config.qrPosition.y - qrPadding,
        config.qrSize + (qrPadding * 2),
        config.qrSize + (qrPadding * 2)
      );

      // 5. DESENHAR QR CODE
      ctx.drawImage(
        qrCanvas,
        offset + config.qrPosition.x,
        offset + config.qrPosition.y,
        config.qrSize,
        config.qrSize
      );

      // 6. INSTRUÇÃO PRINCIPAL
      const instrY = offset + config.qrPosition.y + config.qrSize + cmToPixels(1.2);
      ctx.fillStyle = config.colors.primary;
      ctx.font = `bold ${cmToPixels(0.5)}px Arial, sans-serif`;
      ctx.textAlign = "center";
      ctx.fillText("Escaneie o QR Code para", centerX, instrY);
      ctx.fillText("abrir o nosso cardápio digital", centerX, instrY + cmToPixels(0.65));

      // Linha decorativa
      const lineInstrY = instrY + cmToPixels(1.4);
      ctx.beginPath();
      ctx.moveTo(centerX - cmToPixels(4), lineInstrY);
      ctx.lineTo(centerX + cmToPixels(4), lineInstrY);
      ctx.strokeStyle = config.colors.accent;
      ctx.lineWidth = cmToPixels(0.05);
      ctx.stroke();

      // 7. PASSO A PASSO (texto otimizado para legibilidade)
      ctx.font = `${cmToPixels(0.38)}px Arial, sans-serif`;
      ctx.fillStyle = config.colors.text;
      ctx.textAlign = "left";
      
      const steps = [
        "Selecione os itens e personalize seu pedido",
        "Revise o carrinho e ajuste as quantidades",
        "Clique em Finalizar Pedido para solicitar a conta"
      ];

      const stepsStartY = lineInstrY + cmToPixels(0.8);
      const lineHeight = cmToPixels(0.65);
      const stepsLeftMargin = offset + cmToPixels(1.8);

      steps.forEach((step, index) => {
        const y = stepsStartY + (index * lineHeight);
        
        // Número do passo em dourado
        ctx.fillStyle = config.colors.accent;
        ctx.font = `bold ${cmToPixels(0.42)}px Arial, sans-serif`;
        ctx.fillText(`${index + 1}.`, stepsLeftMargin, y);
        
        // Texto do passo
        ctx.fillStyle = config.colors.text;
        ctx.font = `${cmToPixels(0.38)}px Arial, sans-serif`;
        ctx.fillText(step, stepsLeftMargin + cmToPixels(0.6), y);
      });

      // 8. RODAPÉ
      const footerY = offset + config.height - cmToPixels(1.5);
      ctx.font = `italic ${cmToPixels(0.35)}px Georgia, serif`;
      ctx.fillStyle = config.colors.accent;
      ctx.textAlign = "center";
      ctx.fillText("Agradecemos a preferência!", centerX, footerY);

      // Pequenos detalhes decorativos no rodapé
      ctx.fillStyle = config.colors.accent;
      ctx.font = `${cmToPixels(0.3)}px Arial, sans-serif`;
      ctx.fillText("✦", centerX - cmToPixels(4), footerY);
      ctx.fillText("✦", centerX + cmToPixels(4), footerY);

      console.log("✅ QR Code de alta resolução gerado com sucesso!");
      console.log(`📐 Dimensões: ${totalWidth}x${totalHeight}px (300 DPI)`);
      console.log(`📏 Tamanho de impressão: ~15cm x 21cm`);
      
      return true;
    } catch (error) {
      console.error("❌ Erro ao gerar QR Code:", error);
      return false;
    }
  }, []);

  const handleDownload = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const filename = params.get("filename") || "qrcode-socialite";
    const canvas = canvasRef.current;

    if (!canvas) {
      console.error("❌ Canvas não encontrado");
      return;
    }

    try {
      // Download em PNG de alta qualidade (sem compressão)
      const link = document.createElement("a");
      link.download = `${decodeURIComponent(filename)}_300dpi.png`;
      link.href = canvas.toDataURL("image/png", 1.0);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setHasDownloaded(true);

      console.log("✅ Download iniciado!");
      console.log("💡 Dica: O arquivo está em 300 DPI, pronto para impressão profissional");
    } catch (error) {
      console.error("❌ Erro ao fazer download:", error);
    }
  }, [location.search]);

  // Callback quando o QR Code terminar de renderizar
  const handleQRReady = useCallback(() => {
    console.log("✅ QR Code base renderizado, aguardando estabilização...");
    setTimeout(() => {
      setQrReady(true);
    }, 300);
  }, []);

  // Effect para gerar preview e arquivo de alta resolução
  useEffect(() => {
    if (!qrReady) return;

    console.log("🎨 Iniciando processo de geração...");
    
    // Primeiro, gerar o preview
    const generatePreview = () => {
      const previewCanvas = previewCanvasRef.current;
      if (!previewCanvas) return false;
      
      console.log("🖼️ Gerando preview...");
      const success = generateFramedQR(previewCanvas);
      if (success) {
        setPreviewGenerated(true);
        console.log("✅ Preview gerado!");
      }
      return success;
    };

    // Depois, gerar o arquivo de alta resolução
    const generateHighRes = () => {
      console.log("📸 Gerando arquivo de alta resolução (300 DPI)...");
      const success = generateFramedQR(canvasRef.current);
      if (success) {
        setIsGenerating(false);
        console.log("✅ Arquivo de alta resolução pronto!");
      }
      return success;
    };

    let attempts = 0;
    const maxAttempts = 5;
    
    const tryGenerate = () => {
      attempts++;
      console.log(`🔄 Tentativa ${attempts}/${maxAttempts}...`);
      
      const previewSuccess = generatePreview();
      const highResSuccess = generateHighRes();
      
      if (previewSuccess && highResSuccess) {
        console.log(`✅ Sucesso na tentativa ${attempts}!`);
      } else if (attempts < maxAttempts) {
        console.log(`⏳ Tentando novamente em 300ms...`);
        setTimeout(tryGenerate, 300);
      } else {
        console.error("❌ Falha após múltiplas tentativas");
        setIsGenerating(false);
      }
    };

    setTimeout(tryGenerate, 200);
  }, [qrReady, generateFramedQR]);

  const params = new URLSearchParams(location.search);
  const content = params.get("content");

  if (!content) {
    return (
      <div style={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        height: "100vh",
        fontFamily: "Arial, sans-serif",
        color: "#666",
        padding: "20px",
        textAlign: "center"
      }}>
        <div>
          <p style={{ fontSize: "18px", marginBottom: "10px", fontWeight: "bold" }}>⚠️ Nenhum conteúdo recebido</p>
          <p style={{ fontSize: "14px", color: "#999", marginBottom: "20px" }}>
            Adicione o parâmetro <code style={{ background: "#f4f4f4", padding: "2px 8px", borderRadius: "4px" }}>?content=URL_DO_MENU</code>
          </p>
          <p style={{ fontSize: "13px", color: "#666", marginTop: "20px" }}>
            <strong>Exemplo:</strong><br/>
            <code style={{ background: "#f4f4f4", padding: "4px 8px", borderRadius: "4px", fontSize: "12px" }}>
              /qr_download?content=https://menu.socialite.com
            </code>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)",
      fontFamily: "Arial, sans-serif",
      padding: "40px 20px"
    }}>
      <div style={{
        maxWidth: "1200px",
        margin: "0 auto"
      }}>
        {/* Header */}
        <div style={{
          textAlign: "center",
          marginBottom: "40px"
        }}>
          <h1 style={{
            fontSize: "32px",
            fontWeight: "bold",
            color: "#1A1A1A",
            margin: "0 0 10px 0"
          }}>
            SOCIALITE
          </h1>
          <p style={{
            fontSize: "16px",
            color: "#C9A961",
            fontStyle: "italic"
          }}>
            ✦ Gerador de QR Code com Moldura ✦
          </p>
        </div>

        {isGenerating ? (
          <div style={{ 
            textAlign: "center",
            background: "white",
            padding: "60px 40px",
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)"
          }}>
            <div style={{
              width: "60px",
              height: "60px",
              border: "5px solid #f3f3f3",
              borderTop: "5px solid #C9A961",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 30px"
            }} />
            <style dangerouslySetInnerHTML={{__html: `
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}} />
            <h2 style={{ 
              color: "#1A1A1A", 
              fontSize: "24px",
              fontWeight: "bold",
              marginBottom: "10px"
            }}>
              Gerando QR Code Profissional
            </h2>
            <p style={{ color: "#666", fontSize: "16px", marginBottom: "5px" }}>
              Alta resolução (300 DPI)
            </p>
            <p style={{ color: "#999", fontSize: "14px" }}>
              Tamanho: 15cm × 21cm | Pronto para impressão
            </p>
          </div>
        ) : (
          <div style={{
            display: "grid",
            gridTemplateColumns: showPreview ? "1fr 400px" : "1fr",
            gap: "30px",
            alignItems: "start"
          }}>
            {/* Preview da Moldura */}
            {showPreview && previewGenerated && (
              <div style={{
                background: "white",
                padding: "30px",
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)"
              }}>
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px"
                }}>
                  <h3 style={{
                    fontSize: "20px",
                    fontWeight: "bold",
                    color: "#1A1A1A",
                    margin: 0
                  }}>
                    🖼️ Preview da Moldura
                  </h3>
                  <button
                    onClick={() => setShowPreview(false)}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "#999",
                      cursor: "pointer",
                      fontSize: "14px"
                    }}
                  >
                    ✕ Fechar
                  </button>
                </div>
                
                <div style={{
                  border: "2px solid #e0e0e0",
                  borderRadius: "8px",
                  overflow: "hidden",
                  background: "#f9f9f9"
                }}>
                  <canvas
                    ref={previewCanvasRef}
                    style={{
                      width: "100%",
                      height: "auto",
                      display: "block"
                    }}
                  />
                </div>
                
                <div style={{
                  marginTop: "15px",
                  padding: "15px",
                  background: "#FFF9E6",
                  borderRadius: "8px",
                  fontSize: "13px",
                  color: "#666"
                }}>
                  <p style={{ margin: "0 0 8px 0", fontWeight: "bold", color: "#9D7E3A" }}>
                    ℹ️ Informação
                  </p>
                  <p style={{ margin: 0, lineHeight: "1.6" }}>
                    Este é apenas um preview. O arquivo baixado terá <strong>resolução 300 DPI</strong> (1772×2480 pixels), 
                    perfeito para impressão profissional em 15cm × 21cm.
                  </p>
                </div>
              </div>
            )}

            {/* Painel de Controle */}
            <div style={{ 
              background: "white",
              padding: "40px",
              borderRadius: "12px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)"
            }}>
              <div style={{
                width: "60px",
                height: "60px",
                background: "linear-gradient(135deg, #4CAF50, #45a049)",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px",
                fontSize: "30px",
                color: "white"
              }}>
                ✓
              </div>
              
              <h2 style={{ 
                color: "#4CAF50", 
                fontSize: "22px", 
                marginBottom: "15px",
                fontWeight: "bold",
                textAlign: "center"
              }}>
                QR Code Gerado com Sucesso!
              </h2>
              
              <p style={{ 
                color: "#666", 
                fontSize: "15px", 
                marginBottom: "30px",
                textAlign: "center"
              }}>
                Arquivo em alta resolução (300 DPI)<br/>
                Pronto para impressão profissional
              </p>
              
              <div style={{
                background: "#f9f9f9",
                padding: "20px",
                borderRadius: "8px",
                marginBottom: "25px",
                textAlign: "left"
              }}>
                <p style={{ 
                  fontSize: "13px", 
                  color: "#666",
                  marginBottom: "12px",
                  fontWeight: "bold"
                }}>
                  📋 Especificações:
                </p>
                <ul style={{
                  fontSize: "13px",
                  color: "#666",
                  margin: "0",
                  paddingLeft: "20px",
                  lineHeight: "1.8"
                }}>
                  <li>Resolução: 300 DPI</li>
                  <li>Dimensões: ~15cm × 21cm (A5)</li>
                  <li>Formato: PNG sem compressão</li>
                  <li>Sangria: 3mm incluída</li>
                  <li>Com moldura decorativa Socialite</li>
                </ul>
              </div>

              <button 
                onClick={handleDownload}
                style={{
                  padding: "14px 32px",
                  background: "linear-gradient(135deg, #C9A961, #9D7E3A)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "15px",
                  fontWeight: "bold",
                  boxShadow: "0 3px 10px rgba(201, 169, 97, 0.3)",
                  transition: "transform 0.2s",
                  width: "100%",
                  marginBottom: "15px"
                }}
                onMouseOver={(e) => e.target.style.transform = "scale(1.02)"}
                onMouseOut={(e) => e.target.style.transform = "scale(1)"}
              >
                📥 Baixar Arquivo (300 DPI)
              </button>

              {!showPreview && previewGenerated && (
                <button 
                  onClick={() => setShowPreview(true)}
                  style={{
                    padding: "12px 32px",
                    background: "white",
                    color: "#C9A961",
                    border: "2px solid #C9A961",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "bold",
                    transition: "all 0.2s",
                    width: "100%"
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = "#C9A961";
                    e.target.style.color = "white";
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = "white";
                    e.target.style.color = "#C9A961";
                  }}
                >
                  🖼️ Ver Preview
                </button>
              )}
              
              {hasDownloaded && (
                <p style={{
                  fontSize: "13px",
                  color: "#4CAF50",
                  textAlign: "center",
                  marginTop: "15px",
                  fontWeight: "bold"
                }}>
                  ✓ Arquivo baixado com sucesso!
                </p>
              )}
            </div>
          </div>
        )}

        {/* QR Code original - tamanho grande para alta qualidade */}
        <div 
          ref={qrCanvasRef}
          style={{ 
            position: "absolute",
            left: "-99999px",
            top: "0",
            width: "1024px",
            height: "1024px"
          }}
        >
          <QRCodeCanvas 
            value={content} 
            size={1024}
            level="H"
            includeMargin={true}
            bgColor="#FFFFFF"
            fgColor="#000000"
          />
        </div>

        {/* Canvas de alta resolução (oculto) */}
        <canvas 
          ref={canvasRef} 
          style={{ display: "none" }} 
        />

        {/* Trigger para detectar quando está pronto */}
        <img
          src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
          onLoad={handleQRReady}
          style={{ display: "none" }}
          alt=""
        />
      </div>
    </div>
  );
};

export default QRDownload;